import { memo } from 'react';

import type { DiffEntry } from './utils';
import { formatValue } from './utils';

interface DiffViewProps {
	diffs: DiffEntry[];
}

const STYLE_MAP = {
	added: { prefix: '+', textClass: 'text-green-600', bgClass: 'bg-green-50' },
	removed: {
		prefix: '\u2212',
		textClass: 'text-red-600',
		bgClass: 'bg-red-50',
	},
	modified: {
		prefix: '~',
		textClass: 'text-amber-600',
		bgClass: 'bg-amber-50',
	},
} as const;

export const DiffView = memo(function DiffView({ diffs }: DiffViewProps) {
	if (diffs.length === 0) return null;

	return (
		<div className="shrink-0 border-t-2 border-blue-200 bg-blue-50/50">
			<div className="px-3 py-1.5">
				<span className="text-xs font-medium text-gray-600">
					{diffs.length} unsaved change{diffs.length !== 1 ? 's' : ''}
				</span>
			</div>
			<div className="max-h-48 overflow-y-auto px-3 pb-2">
				<div className="flex flex-col gap-0.5 font-mono text-xs">
					{diffs.map((diff) => {
						const style = STYLE_MAP[diff.type];
						return (
							<div
								key={diff.path + diff.type}
								className={`flex gap-2 rounded px-2 py-0.5 ${style.bgClass}`}
							>
								<span className={`shrink-0 font-bold ${style.textClass}`}>
									{style.prefix}
								</span>
								<span className="min-w-0 flex-1">
									<span className="text-gray-500">{diff.path}: </span>
									{diff.type === 'modified' ? (
										<>
											<span className="text-red-500">
												{formatValue(diff.oldValue)}
											</span>
											<span className="text-gray-400"> → </span>
											<span className="text-green-500">
												{formatValue(diff.newValue)}
											</span>
										</>
									) : diff.type === 'added' ? (
										<span className={style.textClass}>
											{formatValue(diff.newValue)}
										</span>
									) : (
										<span className={style.textClass}>
											{formatValue(diff.oldValue)}
										</span>
									)}
								</span>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
});
